// MS-5 — "La memoria del tuo lavoro": presentational replica of the real Job detail UI.
// Static mock only: no data fetching, no new capability, no backend.
import { ListTree, Image as ImageIcon, PlusCircle, History } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

const items = [
  { desc: "Verniciatura ringhiera balcone", qty: "2,6", unit: "m", total: "€ 260,00" },
  { desc: "Sostituzione piastrelle danneggiate", qty: "4", unit: "pz", total: "€ 96,00" },
];

const photos = [
  { src: "/landing/balcone.jpg", caption: "Balcone: 2,40 × 1,10 m" },
  { src: "/landing/ringhiera.jpg", caption: "Ringhiera prima" },
  { src: "/landing/balcone2.jpg", caption: "Lavoro finito" },
];

const timeline = [
  { label: "Lavoro creato", when: "12/06 · 09:42" },
  { label: "Foto aggiunta", when: "12/06 · 11:20" },
  { label: "Extra confermato", when: "13/06 · 16:05" },
];

const MiniSection = ({ title, icon: Icon, count, children }) => (
  <div className="mt-5 first:mt-0">
    <div className="mb-2 flex items-center gap-2">
      <Icon className="h-4 w-4 text-neutral-400" />
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
        {title}
      </h4>
      {count != null && (
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-600">
          {count}
        </span>
      )}
    </div>
    {children}
  </div>
);

export const JobMemory = () => (
  <div data-testid="landing-job-memory" className="relative">
    <div className="relative grid grid-cols-1 items-center gap-14 lg:grid-cols-[1.25fr_0.75fr] lg:gap-16">
    <div>
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-600 sm:text-base">
        La memoria del tuo lavoro
      </p>
      <h2 className="mt-5 max-w-xl text-3xl font-extrabold leading-[1.15] tracking-tight text-neutral-950 sm:text-4xl">
        Non arrivare a sera cercando di ricordare tutto.
      </h2>
      <p className="mt-7 max-w-xl text-base leading-relaxed text-neutral-700 md:text-lg">
        Foto, misure, aggiornamenti, extra e quello che hai concordato durante la giornata
        restano organizzati intorno al lavoro. Tu vai avanti. Mastro tiene il filo.
      </p>
      <p className="mt-10 max-w-xl border-l-4 border-teal-500 pl-5 text-lg font-extrabold tracking-tight text-neutral-950 md:text-xl">
        Ogni lavoro conserva la sua storia.
      </p>
    </div>

    {/* Replica statica della schermata Lavoro */}
    <div
      aria-hidden="true"
      className="w-full rounded-2xl border border-neutral-200/80 bg-white p-4 shadow-[0_4px_18px_-8px_rgba(15,23,42,0.10)] sm:p-5 lg:ml-auto lg:max-w-[420px]"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-bold tracking-tight text-neutral-950">Mario Rossi</div>
          <div className="mt-0.5 text-xs text-neutral-500">Lavoro Mario Rossi a Via Roma 12</div>
        </div>
        <StatusBadge status="ACTIVE" />
      </div>

      <MiniSection title="Lavorazioni" icon={ListTree} count={items.length}>
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.desc}
              className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-[13px]"
            >
              <span className="font-medium text-neutral-900">{it.desc}</span>
              <span className="shrink-0 text-neutral-500">
                {it.qty} {it.unit} · <b className="text-neutral-800">{it.total}</b>
              </span>
            </div>
          ))}
        </div>
      </MiniSection>

      <MiniSection title="Foto" icon={ImageIcon} count={photos.length}>
        <div className="grid grid-cols-3 gap-2.5">
          {photos.map((p) => (
            <div key={p.src}>
              <img
                src={p.src}
                alt=""
                draggable={false}
                className="h-20 w-full rounded-lg border border-neutral-200 object-cover sm:h-24"
              />
              <span className="mt-1 block truncate text-[11px] text-neutral-500">
                {p.caption}
              </span>
            </div>
          ))}
        </div>
      </MiniSection>

      <MiniSection title="Extra / Varianti" icon={PlusCircle} count={1}>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-[13px]">
          <span className="text-neutral-800">Stuccatura muretto lato scala</span>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-neutral-500">€ 80,00</span>
            <StatusBadge status="CONFIRMED" />
          </div>
        </div>
      </MiniSection>

      <MiniSection title="Cronologia" icon={History} count={timeline.length}>
        <ol className="relative space-y-2.5 border-l border-neutral-200 pl-4">
          {timeline.map((t) => (
            <li key={t.label} className="relative text-[13px]">
              <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-teal-500" />
              <span className="font-medium text-neutral-800">{t.label}</span>
              <div className="text-[11px] text-neutral-400">{t.when}</div>
            </li>
          ))}
        </ol>
      </MiniSection>
    </div>
    </div>
  </div>
);
